import type { EmailAddress, AttachmentMeta, LinkableEmail } from '../types'

// ── Item access ───────────────────────────────────────────────────────────────

/**
 * Returns a snapshot of the current mailbox item's synchronously-available fields.
 * IMPORTANT: Always call this inside Office.onReady and after each ItemChanged event.
 * Do NOT cache the result across email selections.
 */
export function getCurrentItemSnapshot(): Omit<LinkableEmail, 'bodyHtml' | 'bodyText' | 'attachments'> | null {
  try {
    const item = Office.context.mailbox.item
    if (!item) return null

    const from = item.from
      ? { email: item.from.emailAddress ?? '', name: item.from.displayName ?? undefined }
      : null

    const toRecipients: EmailAddress[] = (item.to ?? [])
      .filter((r) => r.emailAddress)
      .map((r) => ({
        email: r.emailAddress!,
        ...(r.displayName ? { name: r.displayName } : {}),
      }))

    const ccRecipients: EmailAddress[] = (item.cc ?? [])
      .filter((r) => r.emailAddress)
      .map((r) => ({
        email: r.emailAddress!,
        ...(r.displayName ? { name: r.displayName } : {}),
      }))

    return {
      internetMessageId: item.internetMessageId ?? null,
      conversationId: item.conversationId ?? null,
      subject: item.subject ?? null,
      from,
      to: toRecipients,
      cc: ccRecipients,
      dateTimeCreated: item.dateTimeCreated?.toISOString() ?? null,
    }
  } catch {
    return null
  }
}

/**
 * Promisified body fetch. Returns null if the call fails or there's no item.
 */
export function getBodyAsync(coercionType: 'html' | 'text' = 'html'): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const item = Office.context.mailbox.item
      if (!item?.body) {
        resolve(null)
        return
      }

      const officeType =
        coercionType === 'html'
          ? Office.CoercionType.Html
          : Office.CoercionType.Text

      item.body.getAsync(officeType, { asyncContext: undefined }, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve((result.value as string) ?? null)
        } else {
          resolve(null)
        }
      })
    } catch {
      resolve(null)
    }
  })
}

/**
 * Returns attachment metadata from the current item.
 * Does NOT fetch content — content upload is Phase 3.5.
 */
export function getAttachments(): AttachmentMeta[] {
  try {
    const item = Office.context.mailbox.item
    if (!item?.attachments) return []

    return (item.attachments as Office.AttachmentDetails[]).map((a) => ({
      id: a.id,
      name: a.name,
      contentType: a.contentType,
      size: a.size,
      isInline: a.isInline,
    }))
  } catch {
    return []
  }
}

/**
 * Subscribe to ItemChanged events (user switches emails).
 * Returns a cleanup function.
 */
export function onItemChanged(handler: () => void): () => void {
  try {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      handler,
      () => { /* callback — no-op */ }
    )
  } catch {
    // Not in an Office environment (e.g., dev browser preview)
  }

  return () => {
    try {
      // Office.js removeHandlerAsync signature accepts AsyncContextOptions only,
      // not a handler reference — so we pass empty options and accept all handlers
      // are removed (no-op if already cleaned up).
      Office.context.mailbox.removeHandlerAsync(
        Office.EventType.ItemChanged,
        {},
        () => { /* callback — no-op */ }
      )
    } catch {
      // Ignore cleanup errors
    }
  }
}

/**
 * Fetches the bytes of a single attachment from the current Outlook item.
 * Returns the raw ArrayBuffer (callers compute sha256 + upload).
 *
 * Office.js returns the content as base64 — we decode here so callers
 * receive a clean buffer they can hash and pass to fetch's PUT body.
 */
export function getAttachmentContentAsync(attachmentId: string): Promise<ArrayBuffer | null> {
  return new Promise((resolve) => {
    try {
      const item = Office.context.mailbox.item
      if (!item || typeof (item as Office.MessageRead).getAttachmentContentAsync !== 'function') {
        resolve(null)
        return
      }
      ;(item as Office.MessageRead).getAttachmentContentAsync(attachmentId, (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          resolve(null)
          return
        }
        const value = result.value as Office.AttachmentContent
        if (!value || value.format !== Office.MailboxEnums.AttachmentContentFormat.Base64) {
          resolve(null)
          return
        }
        try {
          const bin = atob(value.content)
          const bytes = new Uint8Array(bin.length)
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
          resolve(bytes.buffer)
        } catch {
          resolve(null)
        }
      })
    } catch {
      resolve(null)
    }
  })
}

/**
 * Checks whether we are running inside a real Office host.
 */
export function isOfficeReady(): boolean {
  try {
    return typeof Office !== 'undefined' && !!Office.context?.mailbox
  } catch {
    return false
  }
}

/**
 * Returns true when the current item is in compose mode (MessageCompose).
 * In read mode item.body has no prependAsync; in compose mode it does.
 */
export function isComposeMode(): boolean {
  try {
    const item = Office.context.mailbox.item as unknown as Record<string, unknown>
    const body = item?.body as Record<string, unknown> | undefined
    return typeof body?.prependAsync === 'function'
  } catch {
    return false
  }
}

/**
 * Promisified body.prependAsync for compose mode.
 * Inserts html at the top of the draft before the user's typed content.
 * Returns true on success.
 */
export function prependBodyAsync(html: string): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const item = Office.context.mailbox.item as Office.MessageCompose
      if (!item?.body?.prependAsync) {
        resolve(false)
        return
      }
      item.body.prependAsync(
        html,
        { coercionType: Office.CoercionType.Html, asyncContext: undefined },
        (result) => {
          resolve(result.status === Office.AsyncResultStatus.Succeeded)
        }
      )
    } catch {
      resolve(false)
    }
  })
}
